import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './SelectButton.module.css';

interface ISelectButtonProps {
  value: string | null;
  placeholder: string;
  leftIcon?: React.ReactNode;
  showClear?: boolean;
  onClear?: () => void;
  onClick: () => void;
  disabled?: boolean;
}

const ChevronIcon = () => (
  <svg
    viewBox="0 0 16 16"
    width="12"
    height="12"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M4 6l4 4 4-4"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ClearIcon = () => (
  <svg viewBox="0 0 16 16" width="10" height="10" aria-hidden="true" focusable="false">
    <path
      d="M4 4l8 8M12 4l-8 8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

export const SelectButton = forwardRef<HTMLButtonElement, ISelectButtonProps>(
  ({ value, placeholder, leftIcon, showClear, onClear, onClick, disabled }, ref) => {
    const { t } = useTranslation();
    return (
      <button
        ref={ref}
        type="button"
        className={styles.button}
        onClick={onClick}
        disabled={disabled}
      >
        {leftIcon && <span className={styles.leftIcon}>{leftIcon}</span>}

        <span
          className={`${styles.text} ${value === null ? styles.textPlaceholder : styles.textValue}`}
        >
          {value ?? placeholder}
        </span>

        <span className={styles.actions}>
          {showClear && onClear && (
            <span
              role="button"
              className={styles.clearBtn}
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              aria-label={t('issues.action.clear')}
              tabIndex={-1}
            >
              <ClearIcon />
            </span>
          )}
          <span className={styles.chevron}>
            <ChevronIcon />
          </span>
        </span>
      </button>
    );
  },
);

SelectButton.displayName = 'SelectButton';
